import React, { useEffect, useState } from "react";

const resolveModuleComponent = (module, displayName) => {
  const ResolvedComponent = module?.default || module;

  if (!ResolvedComponent) {
    throw new Error(
      `${displayName || "Async component"} did not resolve to a React component.`,
    );
  }

  return ResolvedComponent;
};

const createAsyncComponent = (
  loadComponent,
  { fallback = null, displayName = "AsyncComponent" } = {},
) => {
  let LoadedComponent = null;
  let loadPromise = null;

  const load = () => {
    if (LoadedComponent) {
      return Promise.resolve(LoadedComponent);
    }

    if (!loadPromise) {
      loadPromise = loadComponent().then((module) => {
        LoadedComponent = resolveModuleComponent(module, displayName);
        return LoadedComponent;
      });
    }

    return loadPromise;
  };

  const AsyncComponent = ({ loadingFallback = fallback, ...props }) => {
    const [Component, setComponent] = useState(() => LoadedComponent);
    const [error, setError] = useState(null);

    useEffect(() => {
      let isMounted = true;

      if (!Component) {
        load()
          .then((ResolvedComponent) => {
            if (isMounted) {
              setComponent(() => ResolvedComponent);
            }
          })
          .catch((loadError) => {
            if (isMounted) {
              setError(loadError);
            }
          });
      }

      return () => {
        isMounted = false;
      };
    }, [Component]);

    if (error) {
      throw error;
    }

    if (!Component) {
      return loadingFallback;
    }

    return <Component {...props} />;
  };

  AsyncComponent.displayName = displayName;
  AsyncComponent.preload = load;

  return AsyncComponent;
};

export default createAsyncComponent;
